import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SecondaryBody } from "@sprout/typography";
import { Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import { useVerification } from "@hooks/use_verification";
import GHR_TASK_CONFIG from "@/config/ghr_task_config";
import ClearVerificationModal from "@components/ClearVerificationModal";
import ArrowUpRightIcon from "@sprout/icons/arrow_up_right";
import FoldIcon from "@sprout/icons/fold";
import UnfoldIcon from "@sprout/icons/unfold";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type Application from "@/types/application";
import type { GhrTask } from "@/types/task";
import { isTaskOverdue } from "@/utils/task_utils";
import { useTimeAgo, composeStatusLabel } from "@utils/time_ago";

const configForTask = (task: GhrTask) =>
  task.taskable_type === "TakeHomeTest" && !task.url
    ? GHR_TASK_CONFIG.ThirdPartyTakeHomeTest
    : GHR_TASK_CONFIG[task.taskable_type];

const TaskLabel = ({ color, children }: { color?: "red" | "blue"; children: React.ReactNode }) => (
  <span className="application-card__task-label">
    {color && (
      <span className={`application-card__task-dot application-card__task-dot--${color}`} />
    )}
    <SecondaryBody className="min-w-0 truncate">{children}</SecondaryBody>
  </span>
);

const TaskRow = ({
  task,
  title,
  color,
  isTopRow = false,
}: {
  task: GhrTask;
  title: string;
  color?: "red" | "blue";
  isTopRow?: boolean;
}) => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const queryClient = useQueryClient();
  const isIdentityVerification = task.taskable_type === "IdentityVerificationTask";

  const { verifyWithClear } = useVerification({
    taskId: task.taskable_id,
    onSuccess: () => {
      setShowVerifyModal(false);
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isIdentityVerification) {
      if (task.status === "processing_data") {
        return;
      }
      if (task.status === "awaiting_user_input" && task.verification_url) {
        window.open(task.verification_url, "_blank", "noopener,noreferrer");
        return;
      }
      setShowVerifyModal(true);
      return;
    }

    if (task.url) {
      window.open(task.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleVerify = async () => {
    setShowVerifyModal(false);
    await verifyWithClear();
  };

  const hasArrow = isIdentityVerification || Boolean(task.url);

  return (
    <>
      <div
        className={
          color
            ? `application-card__task-row application-card__task-row--status application-card__task-row--${color}`
            : "application-card__task-row application-card__task-row--detail"
        }
        onClick={isTopRow ? undefined : handleAction}
      >
        <TaskLabel color={color}>{title}</TaskLabel>
        {hasArrow &&
          (isTopRow ? (
            <button type="button" className="application-card__task-arrow" onClick={handleAction}>
              <ArrowUpRightIcon size="xs" />
            </button>
          ) : (
            <span className="application-card__task-arrow">
              <ArrowUpRightIcon size="xs" />
            </span>
          ))}
      </div>
      {isIdentityVerification && (
        <ClearVerificationModal
          isOpen={showVerifyModal}
          onClose={() => setShowVerifyModal(false)}
          onVerify={handleVerify}
          companyName={task.company_name}
        />
      )}
    </>
  );
};

const SummaryRow = ({
  color,
  count,
  expanded,
  onToggle,
}: {
  color: "red" | "blue";
  count: number;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTranslation("tasks");

  return (
    <div
      className={`application-card__task-row application-card__task-row--status application-card__task-row--${color} application-card__pending-summary`}
    >
      <TaskLabel color={color}>{t("pending.tasks", { count })}</TaskLabel>
      <button
        type="button"
        className="application-card__expand-toggle"
        aria-expanded={expanded}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      >
        {expanded ? <FoldIcon size="xs" /> : <UnfoldIcon size="xs" />}
      </button>
    </div>
  );
};

const PendingTasks = ({
  tasks,
  expandAsPopover = false,
}: {
  tasks: GhrTask[];
  expandAsPopover?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const color: "red" | "blue" = tasks.some(isTaskOverdue) ? "red" : "blue";
  const { t } = useTranslation("tasks");
  const titleForTask = (task: GhrTask) => t(configForTask(task).titleKey);

  if (tasks.length === 1) {
    return (
      <div className="application-card__tasks">
        <TaskRow task={tasks[0]} title={titleForTask(tasks[0])} color={color} isTopRow />
      </div>
    );
  }

  const detailRows = tasks.map((task) => (
    <TaskRow key={task.id} task={task} title={titleForTask(task)} />
  ));

  if (expandAsPopover) {
    return (
      <div className="application-card__tasks">
        <Popover open={expanded} onOpenChange={setExpanded}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`application-card__task-row application-card__task-row--status application-card__task-row--${color} application-card__pending-summary`}
              onClick={(e) => e.stopPropagation()}
            >
              <TaskLabel color={color}>{t("pending.tasks", { count: tasks.length })}</TaskLabel>
              <span className="application-card__expand-toggle">
                {expanded ? <FoldIcon size="xs" /> : <UnfoldIcon size="xs" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            avoidCollisions={false}
            align="start"
            sideOffset={6}
            className="application-card__tasks-popover w-(--radix-popover-trigger-width) p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Stack gap="4px">{detailRows}</Stack>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Stack gap="4px" className="application-card__tasks">
      <SummaryRow
        color={color}
        count={tasks.length}
        expanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
      />
      {expanded && detailRows}
    </Stack>
  );
};

const ApplicationCardTasks = ({
  application,
  pendingTasks,
  fallback,
  expandAsPopover = false,
}: {
  application: Application;
  pendingTasks: GhrTask[];
  fallback: React.ReactNode;
  expandAsPopover?: boolean;
}) => {
  const { t } = useTranslation("tasks");
  const completedTaskLabel = composeStatusLabel(
    t(`completed.${application.taskCompletedKey}`),
    useTimeAgo(application.taskCompletedAt),
  );
  const tasks = pendingTasks
    .filter((task) => GHR_TASK_CONFIG[task.taskable_type])
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (tasks.length === 0) {
    return application.taskCompletedKey && application.taskCompletedAt ? (
      <div className="application-card__tasks">
        <div className="application-card__task-row application-card__task-row--completed">
          <SecondaryBody metadata className="min-w-0 truncate">
            {completedTaskLabel}
          </SecondaryBody>
        </div>
      </div>
    ) : (
      <>{fallback}</>
    );
  }

  return <PendingTasks tasks={tasks} expandAsPopover={expandAsPopover} />;
};

export default ApplicationCardTasks;
